import { useState, useEffect } from 'react';
import api from '../services/api';

/**
 * Hook que retorna actions do usuario logado via /api/rbac/me/actions.
 * Cache em memoria (modulo) — uma fetch por sessao do tab.
 *
 * Uso: const { hasAction, actions, loading } = useUserActions();
 *      if (!hasAction('license.bump')) return null;
 *
 * Retorna `loading: true` no primeiro fetch — caller geralmente renderiza
 * algo neutro (skeleton ou nada) ate carregar.
 *
 * Limpa cache: window.__rbacActionsCache = null
 */
let _cache = null;
let _inflight = null;

async function fetchActions() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const res = await api.get('/rbac/me/actions');
      _cache = Array.isArray(res.data) ? res.data : (res.data?.actions || []);
      return _cache;
    } catch {
      _cache = []; // sem auth ou erro: zero actions (failsafe)
      return _cache;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

export function clearActionsCache() {
  _cache = null;
  _inflight = null;
}

export function useUserActions() {
  const [actions, setActions] = useState(_cache || null);
  const [loading, setLoading] = useState(_cache == null);

  useEffect(() => {
    let cancelled = false;
    if (_cache) {
      setActions(_cache);
      setLoading(false);
      return;
    }
    fetchActions().then((a) => {
      if (!cancelled) {
        setActions(a);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const hasAction = (code) => {
    if (!code) return true;
    if (!actions) return false; // ainda carregando = nao
    if (Array.isArray(code)) {
      return code.every((c) => actions.includes(c));
    }
    return actions.includes(code);
  };
  const hasAnyAction = (codes) => {
    if (!codes || codes.length === 0) return true;
    if (!actions) return false;
    return codes.some((c) => actions.includes(c));
  };

  return { actions: actions || [], loading, hasAction, hasAnyAction };
}
